/**
 * MilestonesSection — "What we're doing about it."
 *
 * Each milestone is a row with: kind toggle (corrective | preventive),
 * description (inline editable), owner (free-text user name; the org-user
 * dropdown can be added later), target_date (date picker), measure_text
 * ("how will we know?"), and a status badge with a "Mark complete" button.
 *
 * "+ Add a step" reveals an inline blank row.
 *
 * "✨ Suggest milestones" is enabled once at least one root cause is on the
 * record. It shows 3-5 suggested milestones with checkboxes and an Apply
 * button that POSTs each selected suggestion to /milestones.
 */

import React, { useEffect, useState } from 'react';
import SectionCard from './SectionCard';
import AiButton from './AiButton';
import Pill from './Pill';

const API = process.env.REACT_APP_API_URL;

const STATUS_TONES = {
  not_started: 'bg-slate-100 text-slate-700 ring-slate-200',
  in_progress: 'bg-blue-50 text-blue-800 ring-blue-200',
  blocked:     'bg-orange-50 text-orange-800 ring-orange-200',
  complete:    'bg-emerald-50 text-emerald-800 ring-emerald-200',
  cancelled:   'bg-slate-100 text-slate-500 ring-slate-200',
};
const STATUS_LABELS = {
  not_started: 'Not started',
  in_progress: 'In progress',
  blocked:     'Blocked',
  complete:    'Complete',
  cancelled:   'Cancelled',
};

// Duration tracker — green if 0–30 days since the milestone opened,
// yellow 31–45, red 45+. Hidden once complete or cancelled. Mirrors the
// old app's MilestoneDurationCard visual.
function DurationChip({ milestone }) {
  if (['complete', 'validated', 'rejected', 'cancelled'].includes(milestone.status)) return null;
  const start = milestone.created_at ? new Date(milestone.created_at) : null;
  if (!start || isNaN(start.getTime())) return null;
  const days = Math.floor((Date.now() - start.getTime()) / 86400000);
  let tone, label;
  if (days <= 30) { tone = 'bg-emerald-50 text-emerald-800 ring-emerald-200'; label = `${days}d open`; }
  else if (days <= 45) { tone = 'bg-amber-50 text-amber-800 ring-amber-200'; label = `${days}d open`; }
  else { tone = 'bg-orange-50 text-orange-800 ring-orange-200'; label = `${days}d open · stale`; }
  return <Pill tone={tone}>{label}</Pill>;
}

function MilestoneRow({ milestone, rootCauses, eligibleUsers, onPatch, onMarkComplete }) {
  const [draftDesc, setDraftDesc] = useState(milestone.description || '');
  const [draftMeasure, setDraftMeasure] = useState(milestone.measure_text || '');
  const [draftOwnerId, setDraftOwnerId] = useState(milestone.owner_user_id || '');
  const [draftDate, setDraftDate] = useState(
    milestone.target_date ? milestone.target_date.slice(0, 10) : ''
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Whenever the parent refreshes, reset local drafts to match.
  useEffect(() => {
    setDraftDesc(milestone.description || '');
    setDraftMeasure(milestone.measure_text || '');
    setDraftOwnerId(milestone.owner_user_id || '');
    setDraftDate(milestone.target_date ? milestone.target_date.slice(0, 10) : '');
  }, [milestone.id, milestone.description, milestone.measure_text, milestone.owner_user_id, milestone.target_date]);

  const patch = async (body) => {
    setBusy(true);
    setError(null);
    try {
      await onPatch(milestone.id, body);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const commitDesc = () => {
    const v = draftDesc.trim();
    const original = (milestone.description || '').trim();
    if (v === original) return;
    patch({ description: v });
    // Phase 5 V4 — record this edit as a correction so the CAP drafter
    // learns this org's preferred phrasing for similar future findings.
    // Fail-soft: a failed correction write must never block the patch.
    if (original && v) {
      fetch(`${API}/api/agent-corrections`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent_id: 'cap_ai',
          target_type: 'cap_milestone',
          target_id: String(milestone.id),
          field: 'cap_milestone_description',
          original_value: original,
          corrected_value: v,
          signal_features: {
            kind: milestone.kind,
            root_cause_id: milestone.root_cause_id || null,
          },
        }),
      }).catch(() => { /* silently swallow — patch already succeeded */ });
    }
  };
  const commitMeasure = () => {
    const v = draftMeasure.trim();
    if (v === (milestone.measure_text || '').trim()) return;
    patch({ measure_text: v || null });
  };
  const commitOwner = (newId) => {
    const v = newId || null;
    if (v === (milestone.owner_user_id || null)) return;
    // Send the picked user_id. owner_name is left untouched in the DB so
    // legacy free-text values from earlier rows are preserved as a fallback
    // display. The dropdown is the only way to set the owner now — no leak,
    // no hardcoding.
    patch({ owner_user_id: v });
  };
  const commitDate = () => {
    const current = milestone.target_date ? milestone.target_date.slice(0, 10) : '';
    if (draftDate === current) return;
    patch({ target_date: draftDate || null });
  };

  return (
    <li className="py-3 first:pt-0 last:pb-0">
      <div className="flex gap-3 items-start">
        <span className="mt-1.5 inline-block w-2 h-2 rounded-full bg-slate-300 shrink-0" />
        <div className="flex-1 min-w-0 space-y-1.5">
          {/* Description */}
          <input
            value={draftDesc}
            onChange={e => setDraftDesc(e.target.value)}
            onBlur={commitDesc}
            disabled={busy}
            placeholder="What is the action?"
            className="w-full rounded-md border border-transparent hover:border-slate-200 focus:border-slate-200 focus:ring-2 focus:ring-slate-900/10 px-2 py-1 text-sm bg-transparent focus:bg-white"
          />
          {/* Measure */}
          <input
            value={draftMeasure}
            onChange={e => setDraftMeasure(e.target.value)}
            onBlur={commitMeasure}
            disabled={busy}
            placeholder="How will we know it worked?"
            className="w-full rounded-md border border-transparent hover:border-slate-200 focus:border-slate-200 focus:ring-2 focus:ring-slate-900/10 px-2 py-1 text-[13px] text-slate-600 bg-transparent focus:bg-white"
          />
          {/* Meta row */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <select
              value={milestone.kind}
              onChange={e => patch({ kind: e.target.value })}
              disabled={busy}
              className="rounded-md border border-slate-200 px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            >
              <option value="corrective">Corrective</option>
              <option value="preventive">Preventive</option>
            </select>
            <select
              value={draftOwnerId}
              onChange={e => { setDraftOwnerId(e.target.value); commitOwner(e.target.value || null); }}
              disabled={busy}
              className="rounded-md border border-slate-200 px-2 py-1 text-[12px] min-w-[10rem] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              title="Owner — pulled from your onboarded team"
            >
              <option value="">No owner yet</option>
              {(eligibleUsers || []).map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
              {/* If the saved owner is somehow not in the eligible list,
                  preserve the value as a stable fallback option. */}
              {draftOwnerId && !(eligibleUsers || []).some(u => u.id === draftOwnerId) && (
                <option value={draftOwnerId}>{milestone.owner_user_name || '(unknown user)'}</option>
              )}
            </select>
            <input
              type="date"
              value={draftDate}
              onChange={e => setDraftDate(e.target.value)}
              onBlur={commitDate}
              disabled={busy}
              className="rounded-md border border-slate-200 px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            />
            {rootCauses.length > 0 && (
              <select
                value={milestone.root_cause_id || ''}
                onChange={e => patch({ root_cause_id: e.target.value || null })}
                disabled={busy}
                className="rounded-md border border-slate-200 px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              >
                <option value="">No linked cause</option>
                {rootCauses.map(rc => (
                  <option key={rc.id} value={rc.id}>
                    {rc.text.length > 40 ? rc.text.slice(0, 40) + '…' : rc.text}
                  </option>
                ))}
              </select>
            )}
            <Pill tone={STATUS_TONES[milestone.status] || STATUS_TONES.not_started}>
              {STATUS_LABELS[milestone.status] || milestone.status}
            </Pill>
            <DurationChip milestone={milestone} />
            {milestone.status !== 'complete' && milestone.status !== 'cancelled' && (
              <button
                type="button"
                onClick={() => onMarkComplete(milestone.id)}
                disabled={busy}
                className="text-[12px] text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
              >
                Mark complete
              </button>
            )}
          </div>
          {error && <p className="text-[12px] text-red-700">{error}</p>}
        </div>
      </div>
    </li>
  );
}

function NewMilestoneRow({ capId, rootCauses, eligibleUsers, onCreated, onCancel }) {
  const [description, setDescription] = useState('');
  const [measure, setMeasure] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [date, setDate] = useState('');
  const [kind, setKind] = useState('corrective');
  const [rcId, setRcId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        kind,
        description: description.trim(),
        measure_text: measure.trim() || null,
        owner_user_id: ownerId || null,
        target_date: date || null,
        root_cause_id: rcId || null,
      };
      const res = await fetch(`${API}/api/pcap/caps/${capId}/milestones`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not add');
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="py-3 rounded-lg ring-1 ring-slate-200 bg-slate-50 px-3 space-y-2">
      <input
        autoFocus
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="What is the action?"
        className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        required
      />
      <input
        value={measure}
        onChange={e => setMeasure(e.target.value)}
        placeholder="How will we know it worked?"
        className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
      />
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={kind}
          onChange={e => setKind(e.target.value)}
          className="rounded-md border border-slate-200 px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        >
          <option value="corrective">Corrective</option>
          <option value="preventive">Preventive</option>
        </select>
        <select
          value={ownerId}
          onChange={e => setOwnerId(e.target.value)}
          className="rounded-md border border-slate-200 px-2 py-1 text-[12px] min-w-[10rem] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          title="Owner — pulled from your onboarded team"
        >
          <option value="">Pick owner</option>
          {(eligibleUsers || []).map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="rounded-md border border-slate-200 px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        {rootCauses.length > 0 && (
          <select
            value={rcId}
            onChange={e => setRcId(e.target.value)}
            className="rounded-md border border-slate-200 px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          >
            <option value="">No linked cause</option>
            {rootCauses.map(rc => (
              <option key={rc.id} value={rc.id}>
                {rc.text.length > 40 ? rc.text.slice(0, 40) + '…' : rc.text}
              </option>
            ))}
          </select>
        )}
      </div>
      {error && <p className="text-[12px] text-red-700">{error}</p>}
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy || !description.trim()}
          className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save step'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-md text-[12px] text-slate-600 hover:bg-slate-100"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function MilestonesSection({ data, onRefresh }) {
  const cap = data.cap;
  const milestones = data.milestones || [];
  const rootCauses = data.root_causes || [];

  const [adding, setAdding] = useState(false);
  const [eligibleUsers, setEligibleUsers] = useState([]);

  // Fetch eligible owners scoped to the CAP's department + org-wide compliance.
  // Re-fetches when the CAP's department changes.
  useEffect(() => {
    const params = new URLSearchParams();
    if (cap?.department_id) params.set('department_id', cap.department_id);
    fetch(`${API}/api/pcap/eligible-users?${params.toString()}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (d.success) setEligibleUsers(d.users || []); })
      .catch(() => {});
  }, [cap?.department_id]);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null); // [{description, measure_text, kind, target_date_offset_days}]
  const [aiPickedRc, setAiPickedRc] = useState('');
  const [aiSelected, setAiSelected] = useState({}); // index -> bool
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyError, setApplyError] = useState(null);

  const patchMilestone = async (id, body) => {
    const res = await fetch(`${API}/api/pcap/milestones/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const d = await res.json();
    if (!res.ok || !d.success) throw new Error(d.message || 'Could not save');
    onRefresh();
  };

  const markComplete = async (id) => {
    try {
      const res = await fetch(`${API}/api/pcap/milestones/${id}/status`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'complete' }),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Could not update');
      onRefresh();
    } catch (err) {
      // Bubble up via a window-scope error: shown inline in the row would be
      // ideal, but markComplete is triggered from the row's button so the row
      // itself doesn't track this. Keep it inline-quiet: log only.
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const askAi = async () => {
    setAiLoading(true);
    setAiError(null);
    setAiSuggestions(null);
    setAiSelected({});
    try {
      const body = aiPickedRc ? { root_cause_id: aiPickedRc } : {};
      const res = await fetch(`${API}/api/pcap/caps/${cap.id}/ai/suggest-milestones`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'AI helper unavailable');
      setAiSuggestions(d.suggestions || []);
    } catch (err) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  const applySelected = async () => {
    if (!aiSuggestions) return;
    const picks = aiSuggestions
      .map((s, i) => (aiSelected[i] ? s : null))
      .filter(Boolean);
    if (picks.length === 0) return;
    setApplyBusy(true);
    setApplyError(null);
    try {
      for (const s of picks) {
        const today = new Date();
        let target_date = null;
        if (typeof s.target_date_offset_days === 'number') {
          const dt = new Date(today.getTime() + s.target_date_offset_days * 86400000);
          target_date = dt.toISOString().slice(0, 10);
        }
        const body = {
          kind: s.kind || 'corrective',
          description: s.description,
          measure_text: s.measure_text || null,
          target_date,
          root_cause_id: aiPickedRc || null,
        };
        const res = await fetch(`${API}/api/pcap/caps/${cap.id}/milestones`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const d = await res.json();
        if (!res.ok || !d.success) throw new Error(d.message || 'Could not save one of the suggestions');
      }
      setAiSuggestions(null);
      setAiSelected({});
      onRefresh();
    } catch (err) {
      setApplyError(err.message);
    } finally {
      setApplyBusy(false);
    }
  };

  return (
    <SectionCard
      title="Remediation"
      hint="Each step is owned by one person, with a deadline and a way to know it worked."
    >
      {milestones.length === 0 && !adding && (
        <p className="text-sm text-slate-400 italic mb-3">No actions logged yet.</p>
      )}

      {milestones.length > 0 && (
        <ul className="divide-y divide-slate-100">
          {milestones.map(m => (
            <MilestoneRow
              key={m.id}
              milestone={m}
              rootCauses={rootCauses}
              eligibleUsers={eligibleUsers}
              onPatch={patchMilestone}
              onMarkComplete={markComplete}
            />
          ))}
        </ul>
      )}

      {adding && (
        <div className="mt-3">
          <NewMilestoneRow
            capId={cap.id}
            rootCauses={rootCauses}
            eligibleUsers={eligibleUsers}
            onCreated={() => { setAdding(false); onRefresh(); }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="px-2.5 py-1 rounded-md text-[12px] text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50"
          >
            + Add a step
          </button>
        )}

        {rootCauses.length > 1 && (
          <select
            value={aiPickedRc}
            onChange={e => setAiPickedRc(e.target.value)}
            className="rounded-md border border-slate-200 px-2 py-1 text-[12px] focus:outline-none focus:ring-2 focus:ring-slate-900/10"
          >
            <option value="">Suggest for any cause</option>
            {rootCauses.map(rc => (
              <option key={rc.id} value={rc.id}>
                {rc.text.length > 40 ? rc.text.slice(0, 40) + '…' : rc.text}
              </option>
            ))}
          </select>
        )}

        <AiButton
          label="Suggest milestones"
          onClick={askAi}
          loading={aiLoading}
          disabled={rootCauses.length === 0}
        />
        {rootCauses.length === 0 && (
          <span className="text-[12px] text-slate-400 italic">
            Confirm a root cause first to enable suggestions.
          </span>
        )}
      </div>

      {aiError && <p className="text-[12px] text-red-700 mt-2">{aiError}</p>}

      {aiSuggestions && aiSuggestions.length > 0 && (
        <div className="mt-3 rounded-lg ring-1 ring-slate-200 bg-slate-50 p-3">
          <p className="text-[12px] font-medium text-slate-700 mb-2">
            Suggested steps. Tick the ones you want and apply them.
          </p>
          <ul className="space-y-2">
            {aiSuggestions.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={!!aiSelected[i]}
                  onChange={e => setAiSelected({ ...aiSelected, [i]: e.target.checked })}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800">{s.description}</p>
                  {s.measure_text && (
                    <p className="text-[12px] text-slate-500">How we'll know: {s.measure_text}</p>
                  )}
                  <p className="text-[12px] text-slate-400">
                    {s.kind === 'preventive' ? 'Preventive' : 'Corrective'}
                    {typeof s.target_date_offset_days === 'number' && (
                      <> · target {s.target_date_offset_days} day{s.target_date_offset_days === 1 ? '' : 's'} from today</>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ul>
          {applyError && <p className="text-[12px] text-red-700 mt-2">{applyError}</p>}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={applySelected}
              disabled={applyBusy || Object.values(aiSelected).every(v => !v)}
              className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {applyBusy ? 'Saving…' : 'Apply selected'}
            </button>
            <button
              type="button"
              onClick={() => { setAiSuggestions(null); setAiSelected({}); }}
              className="px-3 py-1.5 rounded-md text-[12px] text-slate-600 hover:bg-slate-100"
            >
              Discard all
            </button>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
